// Considered: a bare ChipAtom row inline in the organism — rejected because this
// molecule composes ScopeCoinAtom (tinted type coin) + ChipAtom (chip container)
// + ButtonAtom ("View all") + StatusBadgeAtom (restricted chip) with type-map
// logic and a drill-in callback; worth isolating + co-testing. See spec Reuse Audit.

import React from 'react';
import { useTranslation } from 'localization';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ScopeCoinAtom from '../../atoms/marketplace/ScopeCoinAtom';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import type { AudienceByType } from 'types/channel';
import {
  SUBSCRIBER_TYPE_HUE,
  SUBSCRIBER_TYPE_GLYPH,
  resolveSubscriberType,
} from './subscriberTypeMeta';
import './SubscriberBreakdownMolecule.scss';

export interface SubscriberBreakdownMoleculeProps {
  byType: AudienceByType[];
  restrictedCount: number;
  onViewAll: () => void;
}

const NS = 'marketplace.channelManage.v3Audience.breakdown';

const SubscriberBreakdownMolecule: React.FC<SubscriberBreakdownMoleculeProps> = ({
  byType,
  restrictedCount,
  onViewAll,
}) => {
  const { t } = useTranslation();

  return (
    <div className="subscriber-breakdown-molecule">
      {byType.length > 0 && (
        <div className="subscriber-breakdown-molecule__chips">
          {byType.map(({ type, count }) => {
            const resolvedType = resolveSubscriberType(type);
            const hue = SUBSCRIBER_TYPE_HUE[resolvedType];
            const glyph = SUBSCRIBER_TYPE_GLYPH[resolvedType];
            const label = t(`${NS}.type.${resolvedType}`, { count });
            return (
              <ChipAtom
                key={type}
                label={label}
                className="subscriber-breakdown-molecule__chip"
                template={
                  <span className="subscriber-breakdown-molecule__chip-content">
                    <ScopeCoinAtom
                      scope="metasphere"
                      glyph={glyph}
                      glyphKind="icon"
                      channelName={label}
                      hue={hue}
                      size="xs"
                    />
                    <span className="p-chip-text">{label}</span>
                  </span>
                }
              />
            );
          })}
        </div>
      )}

      <div className="subscriber-breakdown-molecule__actions">
        <ButtonAtom
          variant="text"
          size="small"
          shape="rectangle"
          label={t(`${NS}.viewAll`)}
          icon="pi pi-chevron-right"
          iconPos="right"
          onClick={onViewAll}
          className="subscriber-breakdown-molecule__view-all-btn"
        />

        {restrictedCount > 0 && (
          <StatusBadgeAtom
            tone="warn"
            icon="pi pi-lock"
            label={t(`${NS}.privateSpheres`, { count: restrictedCount })}
            className="subscriber-breakdown-molecule__restricted-badge"
          />
        )}
      </div>
    </div>
  );
};

SubscriberBreakdownMolecule.displayName = 'SubscriberBreakdownMolecule';
export default SubscriberBreakdownMolecule;
